import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { auth } from '../../../firebase/firebaseConfig';
import { functionsBaseUrl } from '../../../config/runtime';
import { buildSubscriptionPaymentRequest } from '../../../utils/paystackSubscription';
import { billingExplanation, billingStateLabel, useBillingAccess } from '../../../utils/useBillingAccess';
import { useDialogFocus } from '../../../v2/useDialogFocus';

export function isPaystackDestination(value) {
    try {
        const url = new URL(value);
        if (url.username || url.password) return false;
        if (url.protocol === 'https:' && (url.hostname === 'paystack.com' || url.hostname.endsWith('.paystack.com'))) return true;
        return process.env.REACT_APP_USE_EMULATORS === 'true'
            && url.origin === 'http://127.0.0.1:9999';
    } catch { return false; }
}

export function getBillingRedirectUrl(data = {}) {
    const url = data.url || data.authorization_url || data.data?.authorization_url;
    if (!isPaystackDestination(url)) throw new Error('A secure Paystack link could not be opened. Please try again or contact support.');
    return url;
}

export default function SubscriptionActions({ user }) {
    const access = useBillingAccess(user);
    const plan = user?.plan || {};
    const [busy, setBusy] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [confirmCancel, setConfirmCancel] = useState(false);
    const activeRequest = useRef(null);
    const mounted = useRef(true);
    const cancelRequestId = useRef(null);
    const closeDialog = useCallback(() => { if (!activeRequest.current) setConfirmCancel(false); }, []);
    useDialogFocus(confirmCancel, closeDialog);
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; activeRequest.current?.abort(); };
    }, []);

    const request = async (operation, method = 'GET', body) => {
        if (activeRequest.current) return;
        const controller = new AbortController();
        activeRequest.current = controller;
        setBusy(operation); setError(''); setMessage('');
        const timeout = setTimeout(() => controller.abort(), 15000);
        try {
            const identity = auth.currentUser;
            if (!identity || identity.uid !== user?.uid) throw new Error('Sign in again before managing your subscription.');
            const token = await identity.getIdToken();
            const response = await fetch(`${functionsBaseUrl}/${operation}`, {
                method, signal: controller.signal,
                headers: { Authorization: `Bearer ${token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
                ...(body ? { body: JSON.stringify(body) } : {}),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(data.error || 'Billing could not be updated. Please try again.');
            if (!mounted.current) return;
            if (operation === 'cancelSubscription') {
                cancelRequestId.current = null;
                setConfirmCancel(false);
                setMessage('Renewal is cancelled. Your paid access continues until its existing end date. No automatic refund has been issued.');
            } else if (operation === 'getBillingStatus') {
                setMessage('Payment status checked. Your account shows the latest verified information.');
            } else {
                window.location.assign(getBillingRedirectUrl(data));
            }
        } catch (failure) {
            if (mounted.current) setError(failure.name === 'AbortError'
                ? 'The request timed out. Check payment status before trying again; a successful change may already have been saved.'
                : failure.message);
        } finally {
            clearTimeout(timeout);
            activeRequest.current = null;
            if (mounted.current) setBusy('');
        }
    };

    const hasSubscription = Boolean(plan.paystackSubscriptionCode);
    const canStartRenewal = access.requiresPayment && access.state !== 'reconciliation_required';
    const date = access.paidThroughAt ? new Intl.DateTimeFormat('en-ZA', { dateStyle: 'medium' }).format(new Date(access.paidThroughAt)) : null;
    return <section id="billing" className="mt-5 rounded border border-[#d7d8d2] bg-white p-6" aria-labelledby="subscription-actions-title">
        <h2 id="subscription-actions-title" className="text-lg font-semibold">Manage subscription</h2>
        <p className="mt-2 font-semibold">{billingStateLabel(access, plan)}</p>
        <p className="mt-2 text-sm leading-6">{billingExplanation(access, plan)}</p>
        <p className="mt-2 text-sm">Monthly subscriptions renew automatically. Cancellation stops future renewal; refunds are reviewed manually through <Link className="underline" to="/contact">support</Link>. Account deletion is separate.</p>
        <div className="mt-4 flex flex-wrap gap-3">
            <button type="button" className="v2-button" disabled={Boolean(busy)} onClick={() => request('getBillingStatus')}>{busy === 'getBillingStatus' ? 'Checking…' : 'Check payment status'}</button>
            {hasSubscription && <button type="button" className="v2-button" disabled={Boolean(busy)} onClick={() => request('getSubscriptionManagementLink')}>{busy === 'getSubscriptionManagementLink' ? 'Opening…' : 'Manage payment details'}</button>}
            {canStartRenewal && <button type="button" className="v2-button v2-button-fill" disabled={Boolean(busy)} onClick={() => request('initializeSubscriptionPaystackTransaction', 'POST', buildSubscriptionPaymentRequest({ email: user.email, planKey: plan.key || plan.paystackPlanType || user.type, callbackUrl: `${window.location.origin}/verify-payment` }))}>{busy === 'initializeSubscriptionPaystackTransaction' ? 'Opening checkout…' : 'Renew subscription'}</button>}
            {hasSubscription && !plan.cancelAtPeriodEnd && <button type="button" className="v2-button" disabled={Boolean(busy)} onClick={event => { event.currentTarget.focus(); setConfirmCancel(true); }}>Cancel renewal</button>}
        </div>
        {error && <p className="mt-4 text-sm text-red-700" role="alert">{error}</p>}
        {message && <p className="mt-4 text-sm" role="status">{message}</p>}
        {confirmCancel && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div role="dialog" aria-modal="true" aria-labelledby="cancel-renewal-title" className="w-full max-w-md rounded border bg-white p-6">
                <h2 id="cancel-renewal-title" className="text-xl font-semibold">Cancel automatic renewal?</h2>
                <p className="mt-3 text-sm leading-6">{date ? `Your listing and new intake remain available until ${date}.` : 'Your existing verified paid period will be honored.'} You can still finish existing work afterwards. This does not delete your account or issue a refund.</p>
                <div className="mt-5 flex flex-wrap gap-3">
                    <button type="button" className="v2-button" disabled={Boolean(busy)} onClick={closeDialog}>Keep renewal</button>
                    <button type="button" className="v2-button v2-button-fill" disabled={Boolean(busy)} onClick={() => { if (!cancelRequestId.current) cancelRequestId.current = crypto.randomUUID(); request('cancelSubscription', 'POST', { requestId: cancelRequestId.current }); }}>{busy === 'cancelSubscription' ? 'Cancelling…' : 'Confirm cancellation'}</button>
                </div>
            </div>
        </div>}
    </section>;
}
